/**
 * THE photo pipeline: how `Adjustments` become pixels.
 *
 * One description feeds three consumers —
 *   • the renderer builds an SVG filter from `adjustmentsPlan()` for the editor preview,
 *   • the worker bakes the same plan into the export derivative with `applyAdjustmentsToPixels()`
 *     (plus `unsharpInPlace()` on a blur it gets from libvips),
 *   • the editor's histogram and "Auto" run `applyAdjustmentsToPixels()` on a thumbnail.
 *
 * Stage order, fixed: white balance → tone → colour matrix → clarity → sharpen. The vignette is an
 * overlay the renderer draws in every mode (see PhotoImage) and is NOT part of the baked plan.
 *
 * Parity contract: the tone stage is a `TONE_SAMPLES`-point table, exactly what SVG
 * `feComponentTransfer type="table"` interpolates, and `toneLut()` expands the same table the same
 * way for the worker. The colour stage is one 3×3 matrix on both sides. So the only differences
 * between screen and print are float rounding and the shape of the two Gaussian blurs.
 */
import type { Adjustments } from './schema';

export const NEUTRAL_ADJUSTMENTS: Readonly<Adjustments> = Object.freeze({
  brightness: 1,
  contrast: 1,
  highlights: 0,
  shadows: 0,
  whites: 0,
  blacks: 0,
  temperature: 0,
  tint: 0,
  saturation: 1,
  grayscale: 0,
  sepia: 0,
  clarity: 0,
  sharpen: 0,
  vignette: 0,
  preset: null,
});

/** A fresh, mutable neutral set (the value a new photo element starts from). */
export function neutralAdjustments(): Adjustments {
  return { ...NEUTRAL_ADJUSTMENTS };
}

/**
 * Fill in anything an older document or a hand-built literal left out. Documents are parsed with
 * defaults on load, but in-memory objects built before a field existed may lack it.
 */
export function completeAdjustments(a: Partial<Adjustments> | null | undefined): Adjustments {
  return { ...NEUTRAL_ADJUSTMENTS, ...(a ?? {}) } as Adjustments;
}

/** Every field the worker bakes, in a stable order (the derivative cache key is built from these). */
export const BAKED_FIELDS = [
  'brightness', 'contrast', 'highlights', 'shadows', 'whites', 'blacks',
  'temperature', 'tint', 'saturation', 'grayscale', 'sepia', 'clarity', 'sharpen',
] as const satisfies readonly (keyof Adjustments)[];

/** True when baking would return the source unchanged (the vignette does not count: it is drawn, not baked). */
export function isNeutralAdjustments(a: Partial<Adjustments> | null | undefined): boolean {
  const c = completeAdjustments(a);
  return BAKED_FIELDS.every((k) => c[k] === NEUTRAL_ADJUSTMENTS[k]);
}

/** Nothing visible at all — neither baked stages nor the vignette. */
export function isIdentityAdjustments(a: Partial<Adjustments> | null | undefined): boolean {
  return isNeutralAdjustments(a) && (a?.vignette ?? 0) === 0;
}

/** Stable, compact key of the baked fields (equal keys ⇒ pixel-identical derivatives). */
export function bakedAdjustmentsKey(a: Partial<Adjustments> | null | undefined): string {
  const c = completeAdjustments(a);
  return BAKED_FIELDS.map((k) => String(round4(c[k]))).join('|');
}

/** Short DOM-safe id for a filter definition; identical adjustments share one definition. */
export function adjustmentsFilterId(a: Partial<Adjustments> | null | undefined, extra = ''): string {
  return `fa-adj-${hash36(bakedAdjustmentsKey(a) + '|' + extra)}`;
}

const round4 = (n: number): number => Math.round(n * 10000) / 10000;

function hash36(s: string): string {
  // FNV-1a, 32-bit — plenty for "did the numbers change", which is all an element id needs
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

// ---------------------------------------------------------------------------
// white balance
// ---------------------------------------------------------------------------

/** How far the temperature slider swings the red and blue gains at ±1. */
export const TEMPERATURE_GAIN = 0.25;
/** How far the tint slider swings the green gain at ±1. */
export const TINT_GAIN = 0.2;

/** Per-channel gains applied before the tone curve: amber pushes red up and blue down; magenta pulls green. */
export function whiteBalanceGains(a: Pick<Adjustments, 'temperature' | 'tint'>): [number, number, number] {
  const t = a.temperature ?? 0;
  const m = a.tint ?? 0;
  return [1 + TEMPERATURE_GAIN * t, 1 - TINT_GAIN * m, 1 - TEMPERATURE_GAIN * t];
}

// ---------------------------------------------------------------------------
// tone
// ---------------------------------------------------------------------------

/** How far ±1 on `whites`/`blacks` moves the end points of the levels ramp. */
export const LEVELS_RANGE = 0.12;
/** Peak lift of the shadow/highlight bells at ±1 (≈ 0.2 on a 0..1 scale). */
const BELL_GAIN = 1.35;

/** Points in the tone table — what the SVG filter interpolates between, and what the worker expands. */
export const TONE_SAMPLES = 64;

/**
 * The tone curve, 0..1 → 0..1. Brightness and contrast keep their CSS meaning; then a levels ramp
 * from the black point to the white point; then two smooth bells that lift or sink the lower and
 * upper halves without touching pure black or pure white.
 */
export function toneCurve(a: Partial<Adjustments>, x: number): number {
  const brightness = a.brightness ?? 1;
  const contrast = a.contrast ?? 1;
  const blacks = a.blacks ?? 0;
  const whites = a.whites ?? 0;
  const shadows = a.shadows ?? 0;
  const highlights = a.highlights ?? 0;

  let y = x * brightness;
  y = (y - 0.5) * contrast + 0.5;

  const bp = -LEVELS_RANGE * blacks;
  const wp = 1 - LEVELS_RANGE * whites;
  y = (y - bp) / Math.max(0.05, wp - bp);
  y = clamp01(y);

  if (shadows !== 0) y += shadows * BELL_GAIN * y * (1 - y) * (1 - y);
  if (highlights !== 0) y += highlights * BELL_GAIN * y * y * (1 - y);
  return clamp01(y);
}

/** True when the tone stage would be the identity (white balance included, since it rides on the same tables). */
export function isNeutralTone(a: Partial<Adjustments>): boolean {
  return (
    (a.brightness ?? 1) === 1 && (a.contrast ?? 1) === 1 && (a.highlights ?? 0) === 0 && (a.shadows ?? 0) === 0 &&
    (a.whites ?? 0) === 0 && (a.blacks ?? 0) === 0 && (a.temperature ?? 0) === 0 && (a.tint ?? 0) === 0
  );
}

/** One `TONE_SAMPLES`-point table per channel, white balance folded in. Values 0..1. */
export function toneTables(a: Partial<Adjustments>, samples = TONE_SAMPLES): [number[], number[], number[]] {
  const gains = whiteBalanceGains({ temperature: a.temperature ?? 0, tint: a.tint ?? 0 });
  const table = (gain: number): number[] => {
    const out: number[] = new Array<number>(samples);
    for (let i = 0; i < samples; i++) out[i] = round4(toneCurve(a, clamp01((i / (samples - 1)) * gain)));
    return out;
  };
  return [table(gains[0]), table(gains[1]), table(gains[2])];
}

/**
 * Expand a table to a 256-entry lookup exactly the way `feComponentTransfer type="table"` does:
 * linear interpolation between the sample points.
 */
export function toneLut(table: readonly number[]): Uint8Array {
  const n = table.length;
  const lut = new Uint8Array(256);
  for (let i = 0; i < 256; i++) {
    const c = i / 255;
    const pos = c * (n - 1);
    const k = Math.min(n - 2, Math.floor(pos));
    const v = table[k]! + (pos - k) * (table[k + 1]! - table[k]!);
    lut[i] = Math.round(clamp01(v) * 255);
  }
  return lut;
}

// ---------------------------------------------------------------------------
// colour matrix
// ---------------------------------------------------------------------------

/** Rec. 709 luma weights — the ones the CSS `saturate()` / `grayscale()` matrices are built on. */
const LR = 0.2126;
const LG = 0.7152;
const LB = 0.0722;

/** The CSS `sepia(1)` colour matrix, row-major. */
const SEPIA: Matrix3 = [
  [0.393, 0.769, 0.189],
  [0.349, 0.686, 0.168],
  [0.272, 0.534, 0.131],
];

export type Matrix3 = [[number, number, number], [number, number, number], [number, number, number]];

const IDENTITY: Matrix3 = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
];

function saturateMatrix(s: number): Matrix3 {
  return [
    [LR + (1 - LR) * s, LG - LG * s, LB - LB * s],
    [LR - LR * s, LG + (1 - LG) * s, LB - LB * s],
    [LR - LR * s, LG - LG * s, LB + (1 - LB) * s],
  ];
}

function sepiaMatrix(t: number): Matrix3 {
  return [0, 1, 2].map((r) => [0, 1, 2].map((c) => IDENTITY[r]![c]! * (1 - t) + SEPIA[r]![c]! * t)) as Matrix3;
}

function multiply(a: Matrix3, b: Matrix3): Matrix3 {
  // (a · b): apply b first, then a
  return [0, 1, 2].map((r) => [0, 1, 2].map((c) => a[r]![0]! * b[0]![c]! + a[r]![1]! * b[1]![c]! + a[r]![2]! * b[2]![c]!)) as Matrix3;
}

/** saturation, then black & white, then sepia — one matrix. */
export function colourMatrix(a: Partial<Adjustments>): Matrix3 {
  const saturation = a.saturation ?? 1;
  const grayscale = a.grayscale ?? 0;
  const sepia = a.sepia ?? 0;
  let m = IDENTITY;
  if (saturation !== 1) m = saturateMatrix(saturation);
  if (grayscale > 0) m = multiply(saturateMatrix(1 - grayscale), m);
  if (sepia > 0) m = multiply(sepiaMatrix(clamp01(sepia)), m);
  return m;
}

export function isIdentityMatrix(m: Matrix3): boolean {
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) if (Math.abs(m[r]![c]! - IDENTITY[r]![c]!) > 1e-9) return false;
  return true;
}

/** The 20 numbers of an SVG `feColorMatrix type="matrix"` for a 3×3 colour matrix. */
export function svgMatrixValues(m: Matrix3): number[] {
  return [
    ...m[0].map(round4), 0, 0,
    ...m[1].map(round4), 0, 0,
    ...m[2].map(round4), 0, 0,
    0, 0, 0, 1, 0,
  ];
}

// ---------------------------------------------------------------------------
// detail (unsharp masks)
// ---------------------------------------------------------------------------

/** Strength of the sharpen mask at slider 1. */
export const SHARPEN_AMOUNT = 1.0;
/** Strength of the clarity mask at slider 1. */
export const CLARITY_AMOUNT = 0.5;
/** Clarity radius as a fraction of the shorter image side — the same on screen and in print. */
export const CLARITY_RADIUS_FRACTION = 0.02;

/** Gaussian sigma of the sharpen mask for an image whose shorter side is `shortSidePx`. */
export function sharpenSigma(shortSidePx: number): number {
  return Math.min(1.5, Math.max(0.6, 0.00025 * shortSidePx));
}

/** Gaussian sigma of the clarity mask for an image whose shorter side is `shortSidePx`. */
export function claritySigma(shortSidePx: number): number {
  return Math.max(1, CLARITY_RADIUS_FRACTION * shortSidePx);
}

// ---------------------------------------------------------------------------
// the plan
// ---------------------------------------------------------------------------

export type AdjustmentStage =
  | { kind: 'tone'; tables: [number[], number[], number[]] }
  | { kind: 'matrix'; matrix: Matrix3 }
  | { kind: 'unsharp'; role: 'clarity' | 'sharpen'; sigma: number; amount: number };

/**
 * The stages to run, in order, for a picture whose shorter side is `shortSidePx` pixels — display
 * pixels for the preview, derivative pixels for the export. Empty when nothing needs doing.
 */
export function adjustmentsPlan(a: Partial<Adjustments> | null | undefined, opts: { shortSidePx: number }): AdjustmentStage[] {
  const c = completeAdjustments(a);
  const out: AdjustmentStage[] = [];
  if (!isNeutralTone(c)) out.push({ kind: 'tone', tables: toneTables(c) });
  const m = colourMatrix(c);
  if (!isIdentityMatrix(m)) out.push({ kind: 'matrix', matrix: m });
  if (c.clarity > 0) out.push({ kind: 'unsharp', role: 'clarity', sigma: claritySigma(opts.shortSidePx), amount: CLARITY_AMOUNT * c.clarity });
  if (c.sharpen > 0) out.push({ kind: 'unsharp', role: 'sharpen', sigma: sharpenSigma(opts.shortSidePx), amount: SHARPEN_AMOUNT * c.sharpen });
  return out;
}

// ---------------------------------------------------------------------------
// pixels
// ---------------------------------------------------------------------------

export type PixelBuffer = Uint8Array | Uint8ClampedArray;

/**
 * Run the tone and colour stages over interleaved 8-bit pixels, in place. `channels` is 3 (RGB)
 * or 4 (RGBA; alpha untouched). Returns true when anything changed.
 */
export function applyAdjustmentsToPixels(data: PixelBuffer, channels: 3 | 4, a: Partial<Adjustments> | null | undefined): boolean {
  const stages = adjustmentsPlan(a, { shortSidePx: 1 }).filter((s) => s.kind !== 'unsharp');
  if (stages.length === 0) return false;
  let luts: [Uint8Array, Uint8Array, Uint8Array] | null = null;
  let m: Matrix3 | null = null;
  for (const s of stages) {
    if (s.kind === 'tone') luts = [toneLut(s.tables[0]), toneLut(s.tables[1]), toneLut(s.tables[2])];
    else if (s.kind === 'matrix') m = s.matrix;
  }
  const n = data.length - (data.length % channels);
  if (luts) {
    const [lr, lg, lb] = luts;
    for (let i = 0; i < n; i += channels) {
      data[i] = lr[data[i]!]!;
      data[i + 1] = lg[data[i + 1]!]!;
      data[i + 2] = lb[data[i + 2]!]!;
    }
  }
  if (m) {
    const [[m00, m01, m02], [m10, m11, m12], [m20, m21, m22]] = m;
    for (let i = 0; i < n; i += channels) {
      const r = data[i]!;
      const g = data[i + 1]!;
      const b = data[i + 2]!;
      data[i] = clamp255(m00 * r + m01 * g + m02 * b);
      data[i + 1] = clamp255(m10 * r + m11 * g + m12 * b);
      data[i + 2] = clamp255(m20 * r + m21 * g + m22 * b);
    }
  }
  return true;
}

/**
 * `data = data + amount · (data − blurred)`, in place — the unsharp mask, exactly the SVG
 * `feComposite operator="arithmetic" k2="1+amount" k3="-amount"` the preview uses. `blurred` is a
 * Gaussian blur of `data` with the same layout.
 */
export function unsharpInPlace(data: PixelBuffer, blurred: PixelBuffer, amount: number, channels: 3 | 4): void {
  const n = Math.min(data.length, blurred.length) - (data.length % channels);
  const k2 = 1 + amount;
  for (let i = 0; i < n; i += channels) {
    data[i] = clamp255(k2 * data[i]! - amount * blurred[i]!);
    data[i + 1] = clamp255(k2 * data[i + 1]! - amount * blurred[i + 1]!);
    data[i + 2] = clamp255(k2 * data[i + 2]! - amount * blurred[i + 2]!);
  }
}

function clamp255(v: number): number {
  const r = Math.round(v);
  return r < 0 ? 0 : r > 255 ? 255 : r;
}

// ---------------------------------------------------------------------------
// histogram + auto
// ---------------------------------------------------------------------------

export interface Histogram {
  r: Uint32Array;
  g: Uint32Array;
  b: Uint32Array;
  /** Rec. 709 luma */
  l: Uint32Array;
  pixels: number;
}

export function histogramOf(data: PixelBuffer, channels: 3 | 4): Histogram {
  const r = new Uint32Array(256);
  const g = new Uint32Array(256);
  const b = new Uint32Array(256);
  const l = new Uint32Array(256);
  const n = data.length - (data.length % channels);
  let pixels = 0;
  for (let i = 0; i < n; i += channels) {
    const pr = data[i]!;
    const pg = data[i + 1]!;
    const pb = data[i + 2]!;
    r[pr]!++;
    g[pg]!++;
    b[pb]!++;
    l[clamp255(LR * pr + LG * pg + LB * pb)]!++;
    pixels++;
  }
  return { r, g, b, l, pixels };
}

/** The 0..1 level at which `fraction` of the pixels lie below. */
export function percentile(h: Uint32Array, pixels: number, fraction: number): number {
  if (pixels === 0) return 0;
  const target = fraction * pixels;
  let acc = 0;
  for (let i = 0; i < 256; i++) {
    acc += h[i]!;
    if (acc >= target) return i / 255;
  }
  return 1;
}

export interface AutoAdjustments {
  blacks: number;
  whites: number;
  temperature: number;
  tint: number;
}

/**
 * A deterministic "Auto": stretch the levels so the darkest and brightest half-percent of the
 * picture reach black and white, and nudge the white balance halfway towards a grey world. No
 * model, no guessing about content — the same picture always gets the same answer.
 */
export function autoAdjustments(h: Histogram): AutoAdjustments {
  const lo = percentile(h.l, h.pixels, 0.005);
  const hi = percentile(h.l, h.pixels, 0.995);
  // blacks lifts when positive, so the black point lo needs −lo / LEVELS_RANGE
  const blacks = clamp1(-lo / LEVELS_RANGE);
  const whites = clamp1((1 - hi) / LEVELS_RANGE);

  const mean = (c: Uint32Array): number => {
    let sum = 0;
    for (let i = 0; i < 256; i++) sum += i * c[i]!;
    return h.pixels > 0 ? sum / h.pixels / 255 : 0.5;
  };
  const mr = Math.max(0.02, mean(h.r));
  const mg = Math.max(0.02, mean(h.g));
  const mb = Math.max(0.02, mean(h.b));
  // gains that would make the means equal, expressed on the sliders, then halved: grey world is a
  // hint, not a law — a sunset should stay a sunset
  const tFromRed = (mg / mr - 1) / TEMPERATURE_GAIN;
  const tFromBlue = (1 - mg / mb) / TEMPERATURE_GAIN;
  const temperature = clampTo(0.5 * ((tFromRed + tFromBlue) / 2), 0.5);
  const gains = whiteBalanceGains({ temperature, tint: 0 });
  const balancedRB = (mr * gains[0] + mb * gains[2]) / 2;
  const tint = clampTo(0.5 * ((1 - balancedRB / mg) / TINT_GAIN), 0.3);
  return { blacks: round2(blacks), whites: round2(whites), temperature: round2(temperature), tint: round2(tint) };
}

const clamp1 = (n: number): number => Math.min(1, Math.max(-1, n));
const clampTo = (n: number, lim: number): number => Math.min(lim, Math.max(-lim, n));
const round2 = (n: number): number => Math.round(n * 100) / 100;
